/**
 * Feedback Management Page
 * Trang quản lý phản hồi từ sinh viên và giảng viên
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tabs,
  Badge,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  SendOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import FeedbackService, { Feedback, FeedbackStats } from '../../../services/feedbackService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const FeedbackManagementPage: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState<{ status?: string; type?: string; portal?: string }>({});
  
  // Modal states
  const [respondModalVisible, setRespondModalVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [responseForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadFeedbacks();
    loadStats();
  }, [pagination.page, pagination.limit, filters]);

  async function loadFeedbacks() {
    setLoading(true);
    try {
      const response = await FeedbackService.getAdminFeedbacks({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      if (response.success) {
        setFeedbacks(response.data);
        setPagination(prev => ({ ...prev, total: response.pagination?.total || 0 }));
      }
    } catch (error: any) {
      message.error('Không thể tải danh sách phản hồi');
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const response = await FeedbackService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  function openRespondModal(feedback: Feedback) {
    setSelectedFeedback(feedback);
    responseForm.setFieldsValue({
      response: feedback.response || '',
      status: feedback.status === 'completed' ? 'completed' : 'completed'
    });
    setRespondModalVisible(true);
  }

  async function handleRespond(values: { response: string; status: string }) {
    if (!selectedFeedback) return;

    setSubmitting(true);
    try {
      await FeedbackService.respondToFeedback(selectedFeedback.id, values);
      message.success('Đã gửi trả lời thành công');
      setRespondModalVisible(false);
      responseForm.resetFields();
      loadFeedbacks();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi gửi trả lời');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateStatus(id: number, status: string) {
    try {
      await FeedbackService.updateStatus(id, status);
      message.success('Đã cập nhật trạng thái');
      loadFeedbacks();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi cập nhật trạng thái');
    }
  }

  async function handleDelete(id: number) {
    try {
      await FeedbackService.deleteFeedback(id);
      message.success('Đã xóa phản hồi');
      loadFeedbacks();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi xóa phản hồi');
    }
  }

  function getStatusTag(status: string) {
    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Chờ xử lý' },
      processing: { color: 'orange', icon: <ExclamationCircleOutlined />, text: 'Đang xử lý' },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Đã xử lý' }
    };
    const cfg = config[status] || config.pending;
    return <Tag color={cfg.color} icon={cfg.icon}>{cfg.text}</Tag>;
  }

  function getTypeTag(type: string) {
    const config: Record<string, { color: string; text: string }> = {
      technical: { color: 'blue', text: 'Kỹ thuật' },
      financial: { color: 'gold', text: 'Tài chính' },
      academic: { color: 'purple', text: 'Học thuật' },
      other: { color: 'default', text: 'Khác' }
    };
    const cfg = config[type] || config.other;
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
  }

  function getPortalTag(portal: string) {
    return portal === 'student' 
      ? <Tag icon={<UserOutlined />} color="cyan">Sinh viên</Tag>
      : portal === 'teacher'
      ? <Tag icon={<TeamOutlined />} color="volcano">Giảng viên</Tag>
      : <Tag>Admin</Tag>;
  }

  const columns: ColumnsType<Feedback> = [
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Nguồn',
      dataIndex: 'portal',
      key: 'portal',
      width: 100,
      render: (portal) => getPortalTag(portal)
    },
    {
      title: 'Người gửi',
      key: 'sender',
      width: 150,
      render: (_, record) => (
        <div>
          <Text strong>{record.fullName || record.username}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.username}</Text>
        </div>
      )
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'subject',
      key: 'subject',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => getTypeTag(type)
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content) => (
        <Tooltip title={content}>
          <Text style={{ maxWidth: 300 }} ellipsis>{content}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Ngày gửi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => date ? new Date(date).toLocaleString('vi-VN') : '-'
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Trả lời">
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => openRespondModal(record)}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <Tooltip title="Đánh dấu đang xử lý">
              <Button
                size="small"
                icon={<ClockCircleOutlined />}
                onClick={() => handleUpdateStatus(record.id, 'processing')}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Xác nhận xóa phản hồi này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>
        <MessageOutlined /> Quản lý Phản hồi
      </Title>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng số phản hồi"
              value={stats?.total || 0}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Chờ xử lý"
              value={stats?.pending || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Đang xử lý"
              value={stats?.processing || 0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Đã xử lý"
              value={stats?.completed || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Trạng thái"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
          >
            <Option value="pending">Chờ xử lý</Option>
            <Option value="processing">Đang xử lý</Option>
            <Option value="completed">Đã xử lý</Option>
          </Select>
          <Select
            placeholder="Loại phản hồi"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          >
            <Option value="technical">Kỹ thuật</Option>
            <Option value="financial">Tài chính</Option>
            <Option value="academic">Học thuật</Option>
            <Option value="other">Khác</Option>
          </Select>
          <Select
            placeholder="Nguồn"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, portal: value }))}
          >
            <Option value="student">Sinh viên</Option>
            <Option value="teacher">Giảng viên</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { loadFeedbacks(); loadStats(); }}>
            Làm mới
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={feedbacks}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} phản hồi`,
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, limit: pageSize || 20 }))
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px 0' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card size="small" title="Nội dung phản hồi" style={{ background: '#f5f5f5' }}>
                      <Paragraph>{record.content}</Paragraph>
                    </Card>
                  </Col>
                  <Col span={12}>
                    {record.response ? (
                      <Card size="small" title="Trả lời" style={{ background: '#e6f7ff', borderColor: '#1890ff' }}>
                        <Paragraph>{record.response}</Paragraph>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Trả lời bởi: {record.responseBy} - {record.responseAt && new Date(record.responseAt).toLocaleString('vi-VN')}
                        </Text>
                      </Card>
                    ) : (
                      <Card size="small" title="Trả lời" style={{ background: '#fff7e6', borderColor: '#faad14' }}>
                        <Text type="secondary">Chưa có trả lời</Text>
                      </Card>
                    )}
                  </Col>
                </Row>
              </div>
            )
          }}
        />
      </Card>

      {/* Respond Modal */}
      <Modal
        title={`Trả lời phản hồi: ${selectedFeedback?.subject}`}
        open={respondModalVisible}
        onCancel={() => setRespondModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedFeedback && (
          <>
            <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
              <Text strong>Từ: </Text>{selectedFeedback.fullName || selectedFeedback.username}
              <br />
              <Text strong>Nội dung: </Text>
              <Paragraph style={{ marginTop: 8 }}>{selectedFeedback.content}</Paragraph>
            </Card>

            <Form form={responseForm} onFinish={handleRespond} layout="vertical">
              <Form.Item
                name="response"
                label="Nội dung trả lời"
                rules={[{ required: true, message: 'Vui lòng nhập nội dung trả lời' }]}
              >
                <TextArea rows={4} placeholder="Nhập nội dung trả lời..." />
              </Form.Item>
              <Form.Item name="status" label="Trạng thái" initialValue="completed">
                <Select>
                  <Option value="processing">Đang xử lý</Option>
                  <Option value="completed">Đã xử lý</Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<SendOutlined />}>
                    Gửi trả lời
                  </Button>
                  <Button onClick={() => setRespondModalVisible(false)}>Hủy</Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackManagementPage;

